/**
 * Product Selection Store
 * Manages which products are activated and which colors are selected
 * Persists during the design session
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { persistStorage } from '@/lib/impersonation-session';
import {
  type PrintContext,
  type CreatePrintContextParams,
  createPrintContext,
} from '@/types/printContext';
import { useUIStore } from './uiStore';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

interface ProductSelection {
  isActive: boolean;
  selectedColors: string[];
  /**
   * The seller's selling price of this product (one price per product,
   * pricing model of 2026-09-19). Undefined = not priced; never 0.
   */
  sellingPrice?: number;
}

// Position type for mockup view selection
export type MockupPosition = 'front' | 'back' | 'back-inside' | string;

// Re-export PrintContext types for convenience
export type { PrintContext, CreatePrintContextParams };
export { createPrintContext };

interface ProductSelectionState {
  // State
  selections: Record<string, ProductSelection>;
  /**
   * Products the seller activated in this session without a price. After
   * the next successful save the store seeds exactly these from the quote's
   * suggestedStartPrice (the same rule the MCP tools seed with). Transient:
   * never persisted, cleared by loadFromDesign.
   */
  sessionActivated: Record<string, true>;
  focusedProductId: string | null;
  focusedColorId: string | null;
  activePosition: MockupPosition;
  activePositionId: string | null; // The actual ProductPosition.id for backend API

  /**
   * Print context of the active template: its rectangle, the area's mm and
   * the template's Pixel Scale. Null until a configured template is shown;
   * there is no default area (R11).
   */
  activePrintContext: PrintContext | null;

  // Computed
  activeProductIds: string[];
  totalVariants: number;

  // Actions
  toggleProduct: (
    productId: string,
    isActive: boolean,
    defaultColorId?: string
  ) => void;
  toggleColor: (productId: string, colorId: string) => void;
  setSellingPrice: (productId: string, price?: number) => void;
  selectAllColors: (productId: string, colorIds: string[]) => void;
  deselectAllColors: (productId: string) => void;
  activateAll: (
    productIds: string[],
    defaultColorIds: Record<string, string>
  ) => void;
  deactivateAll: () => void;
  reset: () => void;
  setFocusedProduct: (
    productId: string | null,
    colorId?: string | null
  ) => void;
  setActivePosition: (
    position: MockupPosition,
    productPositionId: string | null,
    printContextParams: CreatePrintContextParams
  ) => void;

  // Getters
  getSelection: (productId: string) => ProductSelection | undefined;
  isProductActive: (productId: string) => boolean;
  isColorSelected: (productId: string, colorId: string) => boolean;

  // Design Integration
  loadFromDesign: (products: DesignProductData[]) => void;
  /**
   * Seed the products activated in this session (still unpriced) with the
   * quote's suggestedStartPrice. Returns how many prices were adopted, so
   * the caller can mark the design dirty for the next save.
   */
  adoptSuggestedPrices: (quotes: SuggestedPriceQuote[]) => number;
  /**
   * Get the save configuration for the given products (the caller decides
   * which ones get a row: master, active selection, custom placements).
   * Returns one entry per id with:
   * - isEnabled: active selection with at least one colour
   * - sellingPrice: only when the seller has set one (never 0; an absent
   *   field keeps the stored price server-side)
   * - colors: only for an active selection (patch semantics: an inactive
   *   row keeps its colours server-side)
   * - positions: canvas data for each position (added by buildEnrichedProductConfig)
   */
  getAllProductsConfig: (productIds: string[]) => ProductConfigData[];
}

// Types for design integration
export interface DesignProductData {
  productId: string;
  isEnabled: boolean;
  /** Stored product price as the API delivers it; null/undefined = unpriced. */
  sellingPrice?: string | number | null;
  colors?: Array<{ productColorId: string }>;
}

export interface SuggestedPriceQuote {
  productId: string;
  sellingPrice: string | null;
  suggestedStartPrice: string | null;
}

export interface ProductConfigData {
  productId: string;
  isEnabled: boolean;
  /** Absent when the seller has not priced the product; never 0 */
  sellingPrice?: number;
  /** Absent for an inactive product: the server keeps the row's colours */
  colors?: Array<{ productColorId: string }>;
}

/** A stored price as a number, or undefined when none is stored. */
function priceOf(
  value: string | number | null | undefined
): number | undefined {
  if (value === null || value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

// ═══════════════════════════════════════════════════════════════════
// STORE
// ═══════════════════════════════════════════════════════════════════

export const useProductSelectionStore = create<ProductSelectionState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        selections: {},
        sessionActivated: {},
        focusedProductId: null,
        focusedColorId: null,
        activePosition: 'front' as MockupPosition,
        activePositionId: null,
        activePrintContext: null,

        // Computed getters (as properties that recalculate)
        get activeProductIds() {
          return Object.entries(get().selections)
            .filter(([, sel]) => sel.isActive)
            .map(([id]) => id);
        },

        get totalVariants() {
          return Object.values(get().selections)
            .filter((sel) => sel.isActive)
            .reduce((sum, sel) => sum + sel.selectedColors.length, 0);
        },

        // Toggle product activation. Activating a product with an empty
        // selection seeds it with defaultColorId, if given, so an active
        // product always has a color to show and publish. The price is not
        // seeded here: an unpriced product is remembered as activated in
        // this session and takes the quote's start price after the save.
        toggleProduct: (productId, isActive, defaultColorId) => {
          set((state) => {
            const current = state.selections[productId] || {
              isActive: false,
              selectedColors: [],
            };

            const selectedColors =
              isActive && current.selectedColors.length === 0 && defaultColorId
                ? [defaultColorId]
                : current.selectedColors;

            return {
              selections: {
                ...state.selections,
                [productId]: {
                  ...current,
                  isActive,
                  selectedColors,
                },
              },
              sessionActivated:
                isActive && current.sellingPrice === undefined
                  ? { ...state.sessionActivated, [productId]: true }
                  : state.sessionActivated,
            };
          });
        },

        // Toggle color selection for a product. An active product must
        // always keep at least one color - otherwise the card would show a
        // color that could not be published - so deselecting the last
        // remaining color of an active product is a no-op.
        toggleColor: (productId, colorId) => {
          set((state) => {
            const current = state.selections[productId] || {
              isActive: true,
              selectedColors: [],
            };

            const isSelected = current.selectedColors.includes(colorId);

            if (
              isSelected &&
              current.isActive &&
              current.selectedColors.length === 1 &&
              current.selectedColors[0] === colorId
            ) {
              return state;
            }

            const newSelectedColors = isSelected
              ? current.selectedColors.filter((c) => c !== colorId)
              : [...current.selectedColors, colorId];

            return {
              selections: {
                ...state.selections,
                [productId]: {
                  ...current,
                  selectedColors: newSelectedColors,
                },
              },
            };
          });
        },

        // Set selling price for a product. A price the seller typed wins
        // over any later start-price seed for this product.
        setSellingPrice: (productId, price) => {
          set((state) => {
            const current = state.selections[productId];
            if (!current) return state;
            const sellingPrice =
              typeof price === 'number' && Number.isFinite(price) && price > 0
                ? price
                : undefined;
            const sessionActivated = { ...state.sessionActivated };
            delete sessionActivated[productId];
            // No price = no key, so a cleared field never serialises as
            // `sellingPrice: undefined` and the payload stays free of it.
            const rest: ProductSelection = {
              isActive: current.isActive,
              selectedColors: current.selectedColors,
            };

            return {
              selections: {
                ...state.selections,
                [productId]:
                  sellingPrice === undefined ? rest : { ...rest, sellingPrice },
              },
              sessionActivated:
                sellingPrice === undefined
                  ? state.sessionActivated
                  : sessionActivated,
            };
          });
        },

        // Select all colors for a product
        selectAllColors: (productId, colorIds) => {
          set((state) => {
            const current = state.selections[productId] || {
              isActive: true,
              selectedColors: [],
            };

            return {
              selections: {
                ...state.selections,
                [productId]: {
                  ...current,
                  isActive: true,
                  selectedColors: colorIds,
                },
              },
            };
          });
        },

        // Deselect all colors for a product
        deselectAllColors: (productId) => {
          set((state) => {
            const current = state.selections[productId];
            if (!current) return state;

            return {
              selections: {
                ...state.selections,
                [productId]: {
                  ...current,
                  selectedColors: [],
                },
              },
            };
          });
        },

        // Activate all products with default first color; prices follow
        // after the save from the quote, like toggleProduct.
        activateAll: (productIds, defaultColorIds) => {
          set((state) => {
            const newSelections = { ...state.selections };
            const sessionActivated = { ...state.sessionActivated };

            for (const productId of productIds) {
              const current = newSelections[productId] || {
                isActive: false,
                selectedColors: [],
              };

              const defaultColor = defaultColorIds[productId];
              const selectedColors =
                current.selectedColors.length > 0
                  ? current.selectedColors
                  : defaultColor
                    ? [defaultColor]
                    : [];

              newSelections[productId] = {
                ...current,
                isActive: true,
                selectedColors,
              };
              if (current.sellingPrice === undefined) {
                sessionActivated[productId] = true;
              }
            }

            return { selections: newSelections, sessionActivated };
          });
        },

        // Deactivate all products
        deactivateAll: () => {
          set((state) => {
            const newSelections = { ...state.selections };

            for (const [productId, selection] of Object.entries(
              newSelections
            )) {
              newSelections[productId] = { ...selection, isActive: false };
            }

            return { selections: newSelections };
          });
        },

        // Reset all selections
        reset: () => {
          set({
            selections: {},
            focusedProductId: null,
            focusedColorId: null,
            activePosition: 'front',
            activePositionId: null,
            activePrintContext: null,
          });
        },

        // Set active mockup position (front/back/etc.)
        // Note: Canvas save/load is handled by CanvasEditor.tsx using canvasStateManager
        setActivePosition: (
          position,
          productPositionId,
          printContextParams
        ) => {
          const state = get();
          const newContext = createPrintContext(printContextParams);
          const current = state.activePrintContext;

          // Check if anything actually changed (not just position slug!)
          // This is important for initial load when position is already 'front' but
          // activePositionId and activePrintContext need to be set
          const positionChanged = state.activePosition !== position;
          const positionIdChanged =
            state.activePositionId !== productPositionId;
          const printContextChanged =
            current === null ||
            current.widthPx !== newContext.widthPx ||
            current.heightPx !== newContext.heightPx ||
            current.pxPerMm !== newContext.pxPerMm ||
            current.widthMm !== newContext.widthMm ||
            current.heightMm !== newContext.heightMm ||
            current.offsetX !== newContext.offsetX ||
            current.offsetY !== newContext.offsetY ||
            current.mockupWidthPx !== newContext.mockupWidthPx ||
            current.mockupHeightPx !== newContext.mockupHeightPx ||
            current.sizeTier !== newContext.sizeTier ||
            // Identity and carry-over settings: a pixel-identical area of
            // another product is a different area for the placement rule
            (current.productId ?? null) !== (newContext.productId ?? null) ||
            (current.productClass ?? null) !==
              (newContext.productClass ?? null) ||
            (current.carryOverPercent ?? null) !==
              (newContext.carryOverPercent ?? null) ||
            (current.carryOverAnchor ?? null) !==
              (newContext.carryOverAnchor ?? null) ||
            (current.carryOverTopOffsetMm ?? null) !==
              (newContext.carryOverTopOffsetMm ?? null);

          if (!positionChanged && !positionIdChanged && !printContextChanged) {
            return; // Nothing changed
          }

          set({
            activePosition: position,
            activePositionId: productPositionId,
            activePrintContext: newContext,
          });
        },

        // Set the focused product for preview
        setFocusedProduct: (productId, colorId) => {
          if (!productId) {
            set({ focusedProductId: null, focusedColorId: null });
            return;
          }

          // If colorId is provided, use it; otherwise try to get first selected color
          let resolvedColorId = colorId;
          if (!resolvedColorId) {
            const selection = get().selections[productId];
            resolvedColorId = selection?.selectedColors?.[0] || null;
          }

          set({
            focusedProductId: productId,
            focusedColorId: resolvedColorId || null,
          });
        },

        // Get selection for a product
        getSelection: (productId) => {
          return get().selections[productId];
        },

        // Check if product is active
        isProductActive: (productId) => {
          return get().selections[productId]?.isActive || false;
        },

        // Check if color is selected
        isColorSelected: (productId, colorId) => {
          return (
            get().selections[productId]?.selectedColors.includes(colorId) ||
            false
          );
        },

        // Load product selections from a saved design
        loadFromDesign: (products) => {
          // Check if we're in single-product mode
          const singleProductMode = useUIStore.getState().singleProductMode;

          const newSelections: Record<string, ProductSelection> = {};

          for (const product of products) {
            const colorIds = product.colors?.map((c) => c.productColorId) || [];
            const sellingPrice = priceOf(product.sellingPrice);

            // Keep original isEnabled state for all products.
            // SingleProductMode only changes which product is focused/edited,
            // it should NOT deactivate other products.
            // Exception: the edit-target product must be active so it appears
            // selected in the UI and gets saved with the design.
            const isEditTarget =
              singleProductMode?.productId === product.productId;
            newSelections[product.productId] = {
              isActive: product.isEnabled || isEditTarget,
              selectedColors: colorIds,
              ...(sellingPrice !== undefined && { sellingPrice }),
            };
          }

          // Determine focused product
          let focusedProduct: (typeof products)[number] | undefined;
          if (singleProductMode) {
            // In single-product mode: focus the target product
            focusedProduct = products.find(
              (p) => p.productId === singleProductMode.productId
            );
          }
          if (!focusedProduct) {
            // Default: first active product
            focusedProduct = products.find((p) =>
              singleProductMode
                ? p.productId === singleProductMode.productId
                : p.isEnabled
            );
          }
          const firstColor =
            focusedProduct?.colors?.[0]?.productColorId || null;

          // Reset position to 'front' when loading a design
          // This ensures consistency with canvasStateManager which uses slugs
          set({
            selections: newSelections,
            // A loaded design carries its prices; nothing is seeded for it.
            sessionActivated: {},
            focusedProductId: focusedProduct?.productId || null,
            focusedColorId: firstColor,
            activePosition: 'front',
            activePositionId: null, // Reset to null, will be set when product is focused
            activePrintContext: null,
          });
        },

        // Seed the start price of the products activated in this session
        adoptSuggestedPrices: (quotes) => {
          let adopted = 0;
          set((state) => {
            const selections = { ...state.selections };
            const sessionActivated = { ...state.sessionActivated };
            for (const quote of quotes) {
              if (!sessionActivated[quote.productId]) continue;
              const current = selections[quote.productId];
              // The seller typed a price meanwhile, or the server already
              // holds one: nothing to seed.
              if (!current || current.sellingPrice !== undefined) {
                delete sessionActivated[quote.productId];
                continue;
              }
              if (quote.sellingPrice !== null) {
                delete sessionActivated[quote.productId];
                continue;
              }
              const start = priceOf(quote.suggestedStartPrice);
              // No recommendation and no known cost: the product stays
              // unpriced until the seller types a price.
              if (start === undefined) continue;
              selections[quote.productId] = { ...current, sellingPrice: start };
              delete sessionActivated[quote.productId];
              adopted += 1;
            }
            return { selections, sessionActivated };
          });
          return adopted;
        },

        // Get the save configuration for the requested products
        getAllProductsConfig: (productIds: string[]) => {
          const { selections } = get();
          const configs: ProductConfigData[] = [];

          for (const productId of productIds) {
            const selection = selections[productId];
            // A plain boolean that still narrows `selection` below
            const isActive = selection?.isActive === true;
            const isEnabled = isActive && selection.selectedColors.length > 0;

            configs.push({
              productId,
              isEnabled,
              // A price only when the seller set one; an absent field keeps
              // the stored price, and 0 is never a price.
              ...(typeof selection?.sellingPrice === 'number' && {
                sellingPrice: selection.sellingPrice,
              }),
              // Only an active selection sends its colours (an empty list
              // when none is picked); an inactive row sends no colors key so
              // the server keeps the colours it has.
              ...(isActive && {
                colors: selection.selectedColors.map((colorId) => ({
                  productColorId: colorId,
                })),
              }),
            });
          }

          return configs;
        },
      }),
      {
        name: 'product-selection-storage',
        // sessionStorage in an impersonation tab, localStorage otherwise.
        storage: persistStorage(),
        // Persist stable data only. Every other field is set by the async
        // loadDesign()/loadFromDesign() path; hydrating those from localStorage
        // restores state from a previous design and focuses the wrong product.
        partialize: (state) => ({
          selections: state.selections,
        }),
        // partialize only governs what is written. Payloads stored before that
        // narrowing still carry the transient fields, and rehydration merges
        // them back in — so drop them explicitly on the way out of v0.
        version: 1,
        migrate: (persisted, version) => {
          const state = persisted as Record<string, unknown>;
          if (version < 1) {
            return { selections: state.selections ?? {} };
          }
          return state;
        },
      }
    ),
    { name: 'ProductSelectionStore' }
  )
);

// ═══════════════════════════════════════════════════════════════════
// SELECTORS
// ═══════════════════════════════════════════════════════════════════

export const selectActiveProducts = (state: ProductSelectionState) =>
  Object.entries(state.selections)
    .filter(([, sel]) => sel.isActive)
    .map(([id]) => id);

export const selectTotalVariants = (state: ProductSelectionState) =>
  Object.values(state.selections)
    .filter((sel) => sel.isActive)
    .reduce((sum, sel) => sum + sel.selectedColors.length, 0);

export const selectProductState =
  (productId: string) => (state: ProductSelectionState) =>
    state.selections[productId];

export const selectFocusedProduct = (state: ProductSelectionState) => ({
  productId: state.focusedProductId,
  colorId: state.focusedColorId,
});

export const selectActivePosition = (state: ProductSelectionState) =>
  state.activePosition;
